import {
  CableType,
  JobFiberNumber,
  JobItem,
  JobItemStatusHistoryEvent,
  JobMstPorts,
  JobStatus,
  JobType,
  JobWithRelations,
} from '@utilities-one/api-sdk';

import { AlphaColor, HexColor, SemanticColor } from '@/config/colors';

export const jobStatusNames = {
  [JobStatus.notStarted]: 'Not started',
  [JobStatus.notVerified]: 'Not verified',
  [JobStatus.rejected]: 'Rejected',
  [JobStatus.verified]: 'Verified',
  [JobStatus.canceled]: 'Canceled',
};

export const jobItemStatusNames = {
  [JobStatus.notStarted]: 'Not started',
  [JobStatus.notVerified]: 'Not verified',
  [JobStatus.rejected]: 'Rejected',
  [JobStatus.verified]: 'Verified',
  [JobStatus.canceled]: 'Canceled',
};

const jobStatusColors: Record<JobStatus, SemanticColor> = {
  [JobStatus.notStarted]: SemanticColor.secondary,
  [JobStatus.notVerified]: SemanticColor.primary,
  [JobStatus.rejected]: SemanticColor.danger,
  [JobStatus.verified]: SemanticColor.success,
  [JobStatus.canceled]: SemanticColor.default,
};

const jobStatusHexColors = {
  [JobStatus.notStarted]: HexColor.secondary,
  [JobStatus.notVerified]: HexColor.primary,
  [JobStatus.rejected]: HexColor.danger,
  [JobStatus.verified]: HexColor.success,
  [JobStatus.canceled]: HexColor.cancel,
};

const jobStatusAlphaColors = {
  [JobStatus.notStarted]: AlphaColor.secondary,
  [JobStatus.notVerified]: AlphaColor.primary,
  [JobStatus.rejected]: AlphaColor.danger,
  [JobStatus.verified]: AlphaColor.success,
  [JobStatus.canceled]: AlphaColor.cancel,
};

export const jobTypeNames: Record<JobType, string> = {
  [JobType.splice]: 'Splice',
  [JobType.aerial]: 'Aerial',
  [JobType.underground]: 'Underground',
  [JobType.other]: 'Other',
  [JobType.aerialSplice]: 'Aerial/Splice',
  [JobType.spliceUnderground]: 'Splice/Underground',
  [JobType.misc]: 'Misc',
  [JobType.fulfillment]: 'Fulfillment',
};

const SPLICING_JOB_TYPES = new Set<JobType>([JobType.splice, JobType.aerialSplice, JobType.spliceUnderground]);
export const isSplicingJobType = (type: JobType): boolean => SPLICING_JOB_TYPES.has(type);

export const jobStatus = (status: JobStatus): { name: string; color: SemanticColor; hex: string; alpha: string } => ({
  name: jobStatusNames[status],
  color: jobStatusColors[status],
  hex: jobStatusHexColors[status],
  alpha: jobStatusAlphaColors[status],
});

export const calcJobStatus = (job: JobWithRelations) => {
  if (!job.jobItems || job.jobItems.length === 0) {
    return JobStatus.notStarted;
  }

  if (
    job.jobItems.every(
      (jobItem) =>
        jobItem.productionStatus === JobStatus.canceled ||
        jobItem.ccStatus === JobStatus.canceled ||
        jobItem.foremanStatus === JobStatus.canceled
    )
  ) {
    return JobStatus.canceled;
  }

  if (
    job.jobItems.some(
      (jobItem) =>
        jobItem.productionStatus === JobStatus.rejected ||
        jobItem.ccStatus === JobStatus.rejected ||
        jobItem.foremanStatus === JobStatus.rejected
    )
  ) {
    return JobStatus.rejected;
  }

  const withoutCanceledAndRejected = job.jobItems.filter(
    (jobItem) =>
      jobItem.productionStatus !== JobStatus.canceled &&
      jobItem.ccStatus !== JobStatus.canceled &&
      jobItem.foremanStatus !== JobStatus.canceled &&
      jobItem.productionStatus !== JobStatus.rejected &&
      jobItem.ccStatus !== JobStatus.rejected &&
      jobItem.foremanStatus !== JobStatus.rejected
  );

  if (
    withoutCanceledAndRejected.length > 0 &&
    withoutCanceledAndRejected.every((jobItem) => jobItem.ccStatus === JobStatus.verified)
  ) {
    return JobStatus.verified;
  }

  return JobStatus.notVerified;
};

export const calculatedJobStatus = (job: JobWithRelations) => {
  return jobStatus(calcJobStatus(job));
};

export const jobFiberNumberValues = [
  JobFiberNumber['12F'],
  JobFiberNumber['24F'],
  JobFiberNumber['48F'],
  JobFiberNumber['72F'],
  JobFiberNumber['96F'],
  JobFiberNumber['144F'],
  JobFiberNumber['216F'],
  JobFiberNumber['288F'],
  JobFiberNumber['432F'],
  JobFiberNumber['864F'],
];

export const jobMstPorts = [JobMstPorts['4P'], JobMstPorts['6P'], JobMstPorts['8P'], JobMstPorts['12P']];

export const cableTypeNames = {
  [CableType.fiber]: 'Fiber',
  [CableType.mst]: 'MST',
};

export const calculateDaysDelayColor = (days: number) => {
  return days < 2 ? SemanticColor.success : days < 4 ? SemanticColor.warning : SemanticColor.danger;
};

export const isCanceledJobItem = (jobItem: Pick<JobItem, 'foremanStatus' | 'ccStatus' | 'productionStatus'>) =>
  [jobItem.foremanStatus, jobItem.ccStatus, jobItem.productionStatus].some((status) => status === JobStatus.canceled);

export const canForemanChangeJobItemStatus = (
  jobItem: Pick<JobItem, 'foremanStatus' | 'ccStatus' | 'productionStatus' | 'isCompanyInvoiced'>
) =>
  !isCanceledJobItem(jobItem) &&
  !jobItem.isCompanyInvoiced &&
  ![jobItem.ccStatus, jobItem.productionStatus].some((status) => status === JobStatus.verified);

export const canProductionChangeJobItemStatus = (
  jobItem: Pick<JobItem, 'foremanStatus' | 'ccStatus' | 'productionStatus' | 'isCompanyInvoiced'>
) =>
  !isCanceledJobItem(jobItem) &&
  !jobItem.isCompanyInvoiced &&
  jobItem.foremanStatus === JobStatus.verified &&
  jobItem.ccStatus !== JobStatus.verified;

export const canCcChangeJobItemStatus = (
  jobItem: Pick<JobItem, 'foremanStatus' | 'ccStatus' | 'productionStatus' | 'isCompanyInvoiced'>
) => !isCanceledJobItem(jobItem) && jobItem.productionStatus === JobStatus.verified && !jobItem.isCompanyInvoiced;

export const jobItemStatusHistoryEventNames: Record<JobItemStatusHistoryEvent, string> = {
  [JobItemStatusHistoryEvent.ccStatus]: 'CC status',
  [JobItemStatusHistoryEvent.foremanStatus]: 'Foreman status',
  [JobItemStatusHistoryEvent.productionStatus]: 'Production status',
  [JobItemStatusHistoryEvent.gigCreated]: 'Gig created',
  [JobItemStatusHistoryEvent.gigUpdated]: 'Gig updated',
  [JobItemStatusHistoryEvent.gigPictureUpload]: 'Gig picture upload',
  [JobItemStatusHistoryEvent.gigFixed]: 'Gig fixed',
  [JobItemStatusHistoryEvent.gigClosed]: 'Gig closed',
  [JobItemStatusHistoryEvent.jobItemPictureUpload]: 'JobItem picture upload',
  [JobItemStatusHistoryEvent.jobItemAssetsAssigned]: 'JobItem Assets assigned',
  [JobItemStatusHistoryEvent.jobItemMaterialAssigned]: 'JobItem Material assigned',
  [JobItemStatusHistoryEvent.jobItemUpdated]: 'JobItem updated',
  [JobItemStatusHistoryEvent.autoCanceled]: 'Auto Cancel',
};
